import { useIonRouter } from '@ionic/react';
import { type RefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { queryActiveShellScrollContent } from '@/core/lib/active-shell-scroll';
import { canonicalizeShellPathname } from '@/core/router/app-paths';

const TOP_EXPAND_PX = 8;
/** Scroll distance that maps 0→1 compact progress (larger = slower). */
const COMPACT_SCROLL_RANGE_PX = 150;
const FULL_SCALE = 1;
const COMPACT_SCALE = 0.88;
/** Ignore upward deltas near the bottom — iOS rubber-band looks like scroll-up. */
const BOTTOM_BOUNCE_GUARD_PX = 72;
const REATTACH_DEBOUNCE_MS = 80;
/** After scroll stops mid-progress, ease to full or compact. */
const SETTLE_MS = 240;
const SCROLL_IDLE_MS = 110;
/** Keep bar full after navigation; ignore scroll deltas from page restore. */
const NAV_GRACE_MS = 220;
/** Slow expand / scrub-release scale transition window. */
const SCALE_SETTLE_MS = 700;
/** Dispatched by the scrub hook so compact progress resets to full. */
export const SHELL_TAB_BAR_EXPAND_EVENT = 'shell-tab-bar-request-expand';
/** Dispatched when scrub ends — keep slow scale settle across tab switches. */
export const SHELL_TAB_BAR_SETTLE_EVENT = 'shell-tab-bar-request-settle';

function isNearScrollBottom(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): boolean {
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  return (
    scrollTop > maxScrollTop ||
    scrollTop >= maxScrollTop - BOTTOM_BOUNCE_GUARD_PX
  );
}

function isContentOnHiddenPage(content: Element): boolean {
  const page = content.closest('.ion-page');
  return page?.classList.contains('ion-page-hidden') ?? true;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

type IonScrollDetail = {
  scrollTop?: number;
};

/**
 * Scroll-linked tab bar scale (Instagram-style): progress tracks scroll delta,
 * then settles to full or compact when scrolling stops mid-way.
 * Resets to full size on every route/page change.
 */
export function useShellTabBarCompact(
  enabled: boolean,
  hostRef: RefObject<HTMLElement | null>,
): void {
  const location = useLocation();
  const ionRouter = useIonRouter();
  const progressRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  /** 1 = was scrolling down (toward compact), -1 = up (toward full). */
  const lastDirectionRef = useRef<1 | -1>(1);
  const rafRef = useRef<number | null>(null);
  const pendingScrollTopRef = useRef<number | null>(null);
  const activeContentRef = useRef<HTMLIonContentElement | null>(null);
  const ignoreScrollUntilRef = useRef(0);
  const scrollElCacheRef = useRef(
    new WeakMap<HTMLIonContentElement, HTMLElement>(),
  );

  const pathname = canonicalizeShellPathname(
    ionRouter.routeInfo?.pathname ?? location.pathname,
  );

  useEffect(() => {
    const hostEl = hostRef.current;

    const applyScale = (progress: number) => {
      const host = hostRef.current ?? hostEl;
      if (!host) {
        return;
      }
      const scale = FULL_SCALE + progress * (COMPACT_SCALE - FULL_SCALE);
      host.style.setProperty('--shell-tab-bar-compact-scale', String(scale));
      host.dataset.compactProgress = progress.toFixed(3);
    };

    if (!enabled) {
      progressRef.current = 0;
      applyScale(0);
      return;
    }

    // Page change: always expand, and ignore restore/scroll jumps briefly.
    // If a scrub-release settle is in progress, keep the slow duration.
    const settling = hostEl?.classList.contains(
      'shell-mobile-tab-bar-host--settling',
    );
    progressRef.current = 0;
    lastDirectionRef.current = -1;
    pendingScrollTopRef.current = null;
    ignoreScrollUntilRef.current =
      performance.now() + (settling ? SCALE_SETTLE_MS : NAV_GRACE_MS);
    if (settling) {
      hostEl?.classList.add('shell-mobile-tab-bar-host--expanding');
    }
    applyScale(0);

    const tabs =
      document.querySelector('.app-shell-tabs-host ion-tabs') ??
      document.querySelector('ion-tabs');
    if (!tabs) {
      return;
    }

    let cancelled = false;
    let settleRaf: number | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const reducedMotion = prefersReducedMotion();

    const setProgress = (next: number) => {
      const clamped = Math.min(1, Math.max(0, next));
      if (Math.abs(clamped - progressRef.current) < 0.0005) {
        progressRef.current = clamped;
        return;
      }
      progressRef.current = clamped;
      applyScale(clamped);
    };

    const cancelSettle = () => {
      if (settleRaf != null) {
        cancelAnimationFrame(settleRaf);
        settleRaf = null;
      }
    };

    const seedScrollBaseline = () => {
      const content = queryActiveShellScrollContent();
      if (!content) {
        lastScrollTopRef.current = 0;
        return;
      }
      void content.getScrollElement().then((scrollEl) => {
        if (cancelled) {
          return;
        }
        scrollElCacheRef.current.set(content, scrollEl);
        lastScrollTopRef.current = scrollEl.scrollTop;
      });
    };

    seedScrollBaseline();
    // Re-seed after Ionic finishes showing the new page.
    const seedTimer = setTimeout(() => {
      if (!cancelled) {
        seedScrollBaseline();
        setProgress(0);
        applyScale(0);
      }
    }, NAV_GRACE_MS);

    const settleToDirection = () => {
      if (performance.now() < ignoreScrollUntilRef.current) {
        setProgress(0);
        return;
      }

      if (idleTimer != null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }

      const progress = progressRef.current;
      if (progress <= 0.001 || progress >= 0.999) {
        setProgress(progress <= 0.5 ? 0 : 1);
        return;
      }

      const target: 0 | 1 = lastDirectionRef.current > 0 ? 1 : 0;

      if (reducedMotion) {
        setProgress(target);
        return;
      }

      cancelSettle();
      const start = progress;
      const startTime = performance.now();

      const tick = (now: number) => {
        if (cancelled) {
          return;
        }
        const t = Math.min(1, (now - startTime) / SETTLE_MS);
        setProgress(start + (target - start) * easeOutCubic(t));
        if (t < 1) {
          settleRaf = requestAnimationFrame(tick);
        } else {
          settleRaf = null;
          setProgress(target);
        }
      };

      settleRaf = requestAnimationFrame(tick);
    };

    const scheduleSettle = () => {
      if (performance.now() < ignoreScrollUntilRef.current) {
        return;
      }
      if (idleTimer != null) {
        clearTimeout(idleTimer);
      }
      idleTimer = setTimeout(() => {
        idleTimer = null;
        settleToDirection();
      }, SCROLL_IDLE_MS);
    };

    let expandTimer: ReturnType<typeof setTimeout> | null = null;

    const clearExpandTimer = () => {
      if (expandTimer != null) {
        clearTimeout(expandTimer);
        expandTimer = null;
      }
    };

    const markExpanding = (ms = SCALE_SETTLE_MS) => {
      const host = hostRef.current ?? hostEl;
      if (!host) {
        return;
      }
      host.classList.add('shell-mobile-tab-bar-host--expanding');
      clearExpandTimer();
      expandTimer = setTimeout(() => {
        expandTimer = null;
        const live = hostRef.current ?? hostEl;
        if (
          live?.classList.contains('shell-mobile-tab-bar-host--scrubbing') ||
          live?.classList.contains('shell-mobile-tab-bar-host--settling')
        ) {
          return;
        }
        live?.classList.remove('shell-mobile-tab-bar-host--expanding');
      }, ms);
    };

    const expandToFull = () => {
      cancelSettle();
      if (idleTimer != null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      lastDirectionRef.current = -1;
      markExpanding();
      setProgress(0);
      applyScale(0);
      seedScrollBaseline();
    };

    const onExpandRequest = () => {
      expandToFull();
    };

    const onSettleRequest = () => {
      const host = hostRef.current ?? hostEl;
      host?.classList.add('shell-mobile-tab-bar-host--settling');
      markExpanding(SCALE_SETTLE_MS);
      // Keep scroll from re-compacting during tab switch + scale settle.
      ignoreScrollUntilRef.current = performance.now() + SCALE_SETTLE_MS;
      lastDirectionRef.current = -1;
      setProgress(0);
      applyScale(0);
      seedScrollBaseline();
      window.setTimeout(() => {
        host?.classList.remove('shell-mobile-tab-bar-host--settling');
      }, SCALE_SETTLE_MS);
    };

    const isScrubbing = () => {
      const host = hostRef.current ?? hostEl;
      return (
        host?.classList.contains('shell-mobile-tab-bar-host--scrubbing') ??
        false
      );
    };

    const applyScroll = (
      scrollTop: number,
      scrollHeight: number,
      clientHeight: number,
    ) => {
      // After navigation: keep full size and adopt the page's scroll baseline.
      if (performance.now() < ignoreScrollUntilRef.current) {
        lastScrollTopRef.current = scrollTop;
        setProgress(0);
        return;
      }

      // During / right after scrub expand: stay full until the user scrolls down.
      if (isScrubbing()) {
        lastScrollTopRef.current = scrollTop;
        setProgress(0);
        return;
      }

      const delta = scrollTop - lastScrollTopRef.current;

      if (scrollTop <= TOP_EXPAND_PX) {
        cancelSettle();
        if (idleTimer != null) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
        lastDirectionRef.current = -1;
        setProgress(0);
        lastScrollTopRef.current = scrollTop;
        return;
      }

      if (Math.abs(delta) < 0.5) {
        lastScrollTopRef.current = scrollTop;
        return;
      }

      cancelSettle();

      if (reducedMotion) {
        if (delta > 0) {
          lastDirectionRef.current = 1;
          setProgress(1);
        } else {
          const bounceRecovery =
            isNearScrollBottom(scrollTop, scrollHeight, clientHeight) ||
            isNearScrollBottom(
              lastScrollTopRef.current,
              scrollHeight,
              clientHeight,
            );
          if (!bounceRecovery) {
            lastDirectionRef.current = -1;
            setProgress(0);
          }
        }
        lastScrollTopRef.current = scrollTop;
        scheduleSettle();
        return;
      }

      if (delta > 0) {
        lastDirectionRef.current = 1;
        setProgress(progressRef.current + delta / COMPACT_SCROLL_RANGE_PX);
      } else {
        const bounceRecovery =
          isNearScrollBottom(scrollTop, scrollHeight, clientHeight) ||
          isNearScrollBottom(
            lastScrollTopRef.current,
            scrollHeight,
            clientHeight,
          );
        if (!bounceRecovery) {
          lastDirectionRef.current = -1;
          setProgress(progressRef.current + delta / COMPACT_SCROLL_RANGE_PX);
        }
      }

      lastScrollTopRef.current = scrollTop;
      scheduleSettle();
    };

    const flushScroll = () => {
      rafRef.current = null;
      const scrollTop = pendingScrollTopRef.current;
      const content = activeContentRef.current;
      pendingScrollTopRef.current = null;
      if (scrollTop == null || !content || cancelled) {
        return;
      }

      const cached = scrollElCacheRef.current.get(content);
      if (cached) {
        applyScroll(scrollTop, cached.scrollHeight, cached.clientHeight);
        return;
      }

      void content.getScrollElement().then((scrollEl) => {
        if (cancelled || activeContentRef.current !== content) {
          return;
        }
        scrollElCacheRef.current.set(content, scrollEl);
        applyScroll(scrollTop, scrollEl.scrollHeight, scrollEl.clientHeight);
      });
    };

    const queueScroll = (content: HTMLIonContentElement, scrollTop: number) => {
      activeContentRef.current = content;
      pendingScrollTopRef.current = scrollTop;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(flushScroll);
      }
    };

    const isShellContentEvent = (
      event: Event,
    ): event is Event & {
      target: HTMLIonContentElement;
    } => {
      const target = event.target;
      if (
        !(target instanceof HTMLElement) ||
        target.tagName !== 'ION-CONTENT'
      ) {
        return false;
      }
      if (!target.classList.contains('shell-page-content')) {
        return false;
      }
      if (isContentOnHiddenPage(target)) {
        return false;
      }
      return true;
    };

    const onIonScroll = (event: Event) => {
      if (!isShellContentEvent(event)) {
        return;
      }

      const detail = (event as unknown as CustomEvent<IonScrollDetail>).detail;
      const scrollTop = detail?.scrollTop;
      if (typeof scrollTop !== 'number') {
        return;
      }

      queueScroll(event.target, scrollTop);
    };

    const onIonScrollEnd = (event: Event) => {
      if (!isShellContentEvent(event)) {
        return;
      }
      settleToDirection();
    };

    const nativeCleanups: Array<() => void> = [];
    let reattachTimer: ReturnType<typeof setTimeout> | null = null;

    const clearNative = () => {
      while (nativeCleanups.length > 0) {
        nativeCleanups.pop()?.();
      }
    };

    const attachNativeFallbacks = () => {
      clearNative();
      const contents = document.querySelectorAll(
        '#main-tabs ion-content.shell-page-content',
      );

      for (const node of contents) {
        const content = node as HTMLIonContentElement;
        void content.getScrollElement().then((scrollEl) => {
          if (cancelled) {
            return;
          }
          scrollElCacheRef.current.set(content, scrollEl);

          const onScroll = () => {
            if (isContentOnHiddenPage(content)) {
              return;
            }
            queueScroll(content, scrollEl.scrollTop);
          };

          scrollEl.addEventListener('scroll', onScroll, { passive: true });
          nativeCleanups.push(() => {
            scrollEl.removeEventListener('scroll', onScroll);
          });
        });
      }
    };

    const scheduleNativeReattach = () => {
      if (reattachTimer != null) {
        clearTimeout(reattachTimer);
      }
      reattachTimer = setTimeout(() => {
        reattachTimer = null;
        attachNativeFallbacks();
        // New page entered the outlet — keep bar full and re-baseline.
        if (performance.now() < ignoreScrollUntilRef.current) {
          seedScrollBaseline();
          setProgress(0);
        }
      }, REATTACH_DEBOUNCE_MS);
    };

    tabs.addEventListener('ionScroll', onIonScroll);
    tabs.addEventListener('ionScrollEnd', onIonScrollEnd);
    hostEl?.addEventListener(SHELL_TAB_BAR_EXPAND_EVENT, onExpandRequest);
    hostEl?.addEventListener(SHELL_TAB_BAR_SETTLE_EVENT, onSettleRequest);
    attachNativeFallbacks();

    const outlet = document.querySelector('#main-tabs');
    const observer =
      outlet == null
        ? null
        : new MutationObserver(() => {
            scheduleNativeReattach();
          });
    if (outlet != null && observer != null) {
      observer.observe(outlet, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      cancelled = true;
      clearTimeout(seedTimer);
      clearExpandTimer();
      tabs.removeEventListener('ionScroll', onIonScroll);
      tabs.removeEventListener('ionScrollEnd', onIonScrollEnd);
      hostEl?.removeEventListener(SHELL_TAB_BAR_EXPAND_EVENT, onExpandRequest);
      hostEl?.removeEventListener(SHELL_TAB_BAR_SETTLE_EVENT, onSettleRequest);
      observer?.disconnect();
      clearNative();
      cancelSettle();
      if (idleTimer != null) {
        clearTimeout(idleTimer);
      }
      if (reattachTimer != null) {
        clearTimeout(reattachTimer);
      }
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      activeContentRef.current = null;
      progressRef.current = 0;
      // Keep settling classes across pathname remounts so scrub-release
      // animation survives the tab switch.
      if (
        hostEl &&
        !hostEl.classList.contains('shell-mobile-tab-bar-host--settling')
      ) {
        hostEl.style.removeProperty('--shell-tab-bar-compact-scale');
        delete hostEl.dataset.compactProgress;
      }
    };
  }, [enabled, pathname, hostRef]);
}
